/** Test-simulator + publish-check panel for Service 360.
 *  Suntronix-only. Shows per-criterion verdicts so admins can validate a
 *  service before publishing. Never creates a transaction.  */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Beaker, Loader2, CheckCircle2, XCircle, ClipboardCheck, Rocket,
} from "lucide-react";

function VerdictRow({ v }) {
  const Icon = v.ok ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <div className="flex items-start gap-2 min-w-0">
        <Icon className={"h-4 w-4 mt-0.5 shrink-0 " + (v.ok ? "text-emerald-600" : "text-rose-600")} strokeWidth={2} />
        <div className="min-w-0">
          <div className="text-slate-800 font-medium">{v.label}</div>
          {v.detail && <div className="text-xs text-slate-500 mt-0.5">{v.detail}</div>}
        </div>
      </div>
    </li>
  );
}

export default function ServiceTestPanel({ code }) {
  const [variants, setVariants] = useState([]);
  const [masters, setMasters] = useState([]);
  const [form, setForm] = useState({ master_company_id: "", country: "IN", variant_code: "", quantity: 1, priority: "normal" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publish, setPublish] = useState(null);
  const [pubBusy, setPubBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/services/${code}`),
      api.get("/master-companies"),
      api.post(`/services/${code}/publish-check`, {}),
    ]).then(([svc, mc, pc]) => {
      setVariants(svc.data.variants || []);
      setMasters(mc.data.items || []);
      setPublish(pc.data);
    }).catch(() => { /* silent — customer roles get 403, panel just hides */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/services/${code}/test`, form);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulator failed");
    } finally { setBusy(false); }
  };

  const rerunPublish = async () => {
    setPubBusy(true);
    try {
      const { data } = await api.post(`/services/${code}/publish-check`, {});
      setPublish(data);
    } catch { /* ignore */ } finally { setPubBusy(false); }
  };

  if (!publish) return null; // Customer roles: hide entirely
  const overallOk = publish.overall_ok;

  return (
    <div className="space-y-4" data-testid="svc-test-panel">
      {/* Publish readiness chip */}
      <div className={"flex items-center justify-between rounded-md border px-4 py-3 " + (overallOk ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50")} data-testid="svc-publish-chip">
        <div className="flex items-center gap-2">
          <Rocket className={"h-4 w-4 " + (overallOk ? "text-emerald-700" : "text-amber-700")} strokeWidth={1.6} />
          <div className="text-sm">
            <div className={"font-semibold " + (overallOk ? "text-emerald-800" : "text-amber-900")}>{publish.status}</div>
            <div className="text-[11px] text-slate-500">Lifecycle: {publish.service.lifecycle_state || "draft"}</div>
          </div>
        </div>
        <button onClick={rerunPublish} disabled={pubBusy} className="text-[11px] text-slate-600 hover:text-slate-900 underline decoration-dotted" data-testid="svc-publish-refresh">
          {pubBusy ? "Rechecking…" : "Re-check"}
        </button>
      </div>

      {/* Publish-check details */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2">Publish readiness</div>
        <ul className="divide-y divide-slate-100">
          {(publish.checks || []).map((c, i) => <VerdictRow key={i} v={c} />)}
        </ul>
      </div>

      {/* Test simulator */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-3 flex items-center gap-2">
          <Beaker className="h-3.5 w-3.5" strokeWidth={1.6} /> Test service configuration
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="text-xs">
            <span className="text-slate-600">Customer</span>
            <select className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={form.master_company_id} onChange={(e) => setForm({ ...form, master_company_id: e.target.value })} data-testid="svc-test-customer">
              <option value="">Any</option>
              {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <label className="text-xs">
            <span className="text-slate-600">Country</span>
            <input className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="svc-test-country" placeholder="IN, UK, AE…" />
          </label>
          <label className="text-xs">
            <span className="text-slate-600">Variant</span>
            <select className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={form.variant_code} onChange={(e) => setForm({ ...form, variant_code: e.target.value })} data-testid="svc-test-variant">
              <option value="">Core (no variant)</option>
              {variants.map((v) => <option key={v.code} value={v.code}>{v.name}</option>)}
            </select>
          </label>
          <label className="text-xs">
            <span className="text-slate-600">Quantity</span>
            <input type="number" min="1" className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 1 })} data-testid="svc-test-qty" />
          </label>
          <label className="text-xs">
            <span className="text-slate-600">Priority</span>
            <select className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="svc-test-priority">
              <option value="normal">Normal</option>
              <option value="priority">Priority</option>
              <option value="critical">Critical</option>
            </select>
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">Simulator runs read-only against the live catalogue. No transaction is created.</div>
          <button onClick={run} disabled={busy} className="h-9 px-3 rounded-md bg-slate-900 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="svc-test-run">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCheck className="h-3.5 w-3.5" />} Run simulation
          </button>
        </div>

        {result && (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="svc-test-result">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Simulation result</div>
              <div className={"text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 " + (result.overall_ok ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>{result.status}</div>
            </div>
            <ul className="divide-y divide-slate-200">
              {(result.verdicts || []).map((v, i) => <VerdictRow key={i} v={v} />)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
